/**
 * ProgressiveComparisonView Component
 *
 * Orchestrates the progressive loading of comparison columns.
 * ALWAYS uses the same rendering path (buildColumnSections) so that
 * the UI structure never changes between single and combined states.
 * When both subjects are loaded, deltas and winners are overlaid
 * on the same sections — no layout jump.
 */

import { AlertCircle, GitCompare } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ComparisonColumn } from './ComparisonColumn'
import { ComparisonColumnSkeleton } from './ComparisonColumnSkeleton'
import type { SectionShape } from './ComparisonColumnSkeleton'
import type {
  ProgressiveComparisonState,
  SubjectWithAggregations,
  SubjectAggregations,
} from '@/types/comparison'
import type { ColumnSection, ColumnMetric } from './ComparisonColumn'

interface ProgressiveComparisonViewProps {
  state: ProgressiveComparisonState
  className?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const toPercent = (val: number, total: number): number =>
  total > 0 ? Math.round((val / total) * 1000) / 10 : 0

const getTopN = <T extends { name: string; count: number }>(
  arr: readonly T[],
  n: number
): T[] => [...arr].sort((a, b) => b.count - a.count).slice(0, n)

function formatTextMetric(name: string, count: number, total: number): string {
  const pct = toPercent(count, total)
  return `${name} — ${pct}%`
}

function buildTopNMetrics(
  items: readonly { name: string; count: number }[],
  n: number,
  prefix?: string
): { label: string; value: string; format: 'text' }[] {
  const total = items.reduce((sum, item) => sum + item.count, 0)
  const top = getTopN(items, n)
  const ordinals = ['1st', '2nd', '3rd', '4th', '5th']

  return Array.from({ length: n }, (_, i) => {
    const item = top[i]
    const name = item ? (prefix ? `${prefix}${item.name}` : item.name) : null
    return {
      label: ordinals[i] ?? `${i + 1}th`,
      value: name ? formatTextMetric(name, item!.count, total) : '—',
      format: 'text' as const,
    }
  })
}

/** Generation definitions for age grouping (matches old transformer). */
const GENERATIONS = [
  { label: 'Gen Alpha (0-12)', maxLower: 12 },
  { label: 'Gen Z (13-24)', maxLower: 24 },
  { label: 'Millennials (25-40)', maxLower: 40 },
  { label: 'Gen X (41-60)', maxLower: 60 },
  { label: 'Boomers (61+)', maxLower: Infinity },
] as const

/**
 * Group raw age brackets (e.g. "(18-24)", "(25-30)") into generations.
 * Parses the lower bound from each bracket name and assigns to a generation.
 */
function groupAgeByGeneration(
  ageBrackets: readonly { name: string; count: number }[]
): { name: string; count: number }[] {
  const genCounts = new Map<string, number>()
  GENERATIONS.forEach((g) => genCounts.set(g.label, 0))

  for (const bracket of ageBrackets) {
    const match = bracket.name.match(/(\d+)/)
    const lowerBound = match ? parseInt(match[1], 10) : 0
    const gen = GENERATIONS.find((g) => lowerBound <= g.maxLower)
    if (gen) {
      genCounts.set(gen.label, (genCounts.get(gen.label) ?? 0) + bracket.count)
    }
  }

  return GENERATIONS.map((g) => ({
    name: g.label,
    count: genCounts.get(g.label) ?? 0,
  }))
}

/** Metrics where lower is better (for winner logic). */
const LOWER_IS_BETTER = ['spam', 'toxic', 'negative', 'toxicity']

function isHigherBetter(label: string): boolean {
  return !LOWER_IS_BETTER.some((kw) => label.toLowerCase().includes(kw))
}

/**
 * Enrich metrics with delta / isWinner when comparing against another column.
 * Only applies to numeric values — text metrics are left untouched.
 */
function enrichWithDeltas(
  metrics: ColumnMetric[],
  otherMetrics: ColumnMetric[]
): ColumnMetric[] {
  return metrics.map((m, i) => {
    const other = otherMetrics[i]
    if (
      typeof m.value !== 'number' ||
      !other ||
      typeof other.value !== 'number'
    ) {
      return m
    }

    const delta = m.value - other.value
    const higherBetter = isHigherBetter(m.label)
    const isWinner = higherBetter ? m.value > other.value : m.value < other.value

    return { ...m, delta, isWinner }
  })
}

// ---------------------------------------------------------------------------
// buildColumnSections — single source of truth for column structure
// ---------------------------------------------------------------------------

/**
 * Build column sections from raw aggregations.
 * Structure matches the backend's ComparisonSection format produced by
 * transformToComparisonSections (see src/utils/comparisonTransformers.ts).
 * Optionally accepts another subject's aggregations to compute deltas.
 */
function buildColumnSections(
  agg: SubjectAggregations,
  otherAgg?: SubjectAggregations | null
): ColumnSection[] {
  const sections: ColumnSection[] = []

  const emptySlots = (n: number): { label: string; value: string; format: 'text' }[] =>
    Array.from({ length: n }, (_, i) => ({
      label: ['1st', '2nd', '3rd', '4th', '5th'][i] ?? `${i + 1}th`,
      value: '—',
      format: 'text' as const,
    }))

  // 1. Vanity Metrics
  sections.push({
    id: 'vanity',
    title: 'Vanity Metrics',
    metrics: [
      { label: 'Total Profiles', value: agg.vanity.totalProfiles, format: 'compact' },
      { label: 'Total Followers', value: agg.vanity.totalFollowers, format: 'compact' },
      { label: 'Avg Followers', value: agg.vanity.avgFollowers, format: 'compact' },
      { label: 'Total Comments', value: agg.vanity.totalComments ?? 0, format: 'compact' },
      { label: 'Total Posts', value: agg.vanity.totalPosts, format: 'compact' },
    ],
  })

  // 2. Sentiment
  sections.push({
    id: 'sentiment',
    title: 'Sentiment',
    metrics: [
      { label: 'Avg Sentiment', value: agg.vanity.avgSentiment, format: 'number' },
      { label: 'Positive', value: agg.comments?.sentiment_positive ?? 0, format: 'compact' },
      { label: 'Neutral', value: agg.comments?.sentiment_neutral ?? 0, format: 'compact' },
      { label: 'Negative', value: agg.comments?.sentiment_negative ?? 0, format: 'compact' },
    ],
  })

  // 3. Demographics — Gender (always show Male, Female)
  const genderTotal = agg.demographics.gender.reduce((s, g) => s + g.count, 0)
  const genderMap = new Map(agg.demographics.gender.map((g) => [g.name.toLowerCase(), g.count]))
  sections.push({
    id: 'demographics_gender',
    title: 'Demographics — Gender',
    metrics: [
      { label: 'Male', value: toPercent(genderMap.get('male') ?? 0, genderTotal), format: 'percent' as const },
      { label: 'Female', value: toPercent(genderMap.get('female') ?? 0, genderTotal), format: 'percent' as const },
    ],
  })

  // 4. Demographics — Age (grouped by generation)
  const ageGenerations = groupAgeByGeneration(agg.demographics.age)
  const ageGenTotal = ageGenerations.reduce((s, a) => s + a.count, 0)
  sections.push({
    id: 'demographics_age',
    title: 'Demographics — Age',
    metrics: GENERATIONS.map((gen) => {
      const genData = ageGenerations.find((a) => a.name === gen.label)
      return {
        label: gen.label,
        value: ageGenTotal > 0 ? toPercent(genData?.count ?? 0, ageGenTotal) : 0,
        format: 'percent' as const,
      }
    }),
  })

  // 5. Demographics — Ethnicity (top 4)
  sections.push({
    id: 'demographics_ethnicity',
    title: 'Demographics — Ethnicity',
    metrics: agg.demographics.ethnicities && agg.demographics.ethnicities.length > 0
      ? buildTopNMetrics(agg.demographics.ethnicities, 4)
      : emptySlots(4),
  })

  // 6. Demographics — Top Countries (top 3)
  sections.push({
    id: 'demographics_countries',
    title: 'Demographics — Top Countries',
    metrics: agg.demographics.countries.length > 0
      ? buildTopNMetrics(agg.demographics.countries, 3)
      : emptySlots(3),
  })

  // 7. Psychographics (lifestyle + status)
  const psychoMetrics: ColumnMetric[] = []
  if (agg.demographics.lifestyle && agg.demographics.lifestyle.length > 0) {
    psychoMetrics.push(...buildTopNMetrics(agg.demographics.lifestyle, 2).map((m) => ({
      ...m,
      label: m.label === '1st' ? 'Top Lifestyle' : '2nd Lifestyle',
    })))
  } else {
    psychoMetrics.push(
      { label: 'Top Lifestyle', value: '—', format: 'text' as const },
      { label: '2nd Lifestyle', value: '—', format: 'text' as const },
    )
  }
  if (agg.demographics.status && agg.demographics.status.length > 0) {
    psychoMetrics.push(...buildTopNMetrics(agg.demographics.status, 2).map((m) => ({
      ...m,
      label: m.label === '1st' ? 'Top Status Class' : '2nd Status Class',
    })))
  } else {
    psychoMetrics.push(
      { label: 'Top Status Class', value: '—', format: 'text' as const },
      { label: '2nd Status Class', value: '—', format: 'text' as const },
    )
  }
  sections.push({ id: 'psychographics', title: 'Psychographics', metrics: psychoMetrics })

  // 8. Follower Tier (always show Nano, Micro, Mid/Medio, Macro, Mega)
  const tierTotal = agg.followerTier.reduce((s, t) => s + t.count, 0)
  const tierMap = new Map(agg.followerTier.map((t) => [t.name.toLowerCase(), t.count]))
  sections.push({
    id: 'follower_tier',
    title: 'Follower Tier',
    metrics: [
      { label: 'Nano', value: toPercent(tierMap.get('nano') ?? 0, tierTotal), format: 'percent' as const },
      { label: 'Micro', value: toPercent(tierMap.get('micro') ?? 0, tierTotal), format: 'percent' as const },
      { label: 'Medio', value: toPercent(tierMap.get('medio') ?? tierMap.get('mid') ?? 0, tierTotal), format: 'percent' as const },
      { label: 'Macro', value: toPercent(tierMap.get('macro') ?? 0, tierTotal), format: 'percent' as const },
      { label: 'Mega', value: toPercent(tierMap.get('mega') ?? 0, tierTotal), format: 'percent' as const },
    ],
  })

  // 9. Top 5 Emotions
  sections.push({
    id: 'emotions',
    title: 'Top 5 Emotions',
    metrics: agg.emotions.length > 0
      ? buildTopNMetrics(agg.emotions, 5)
      : emptySlots(5),
  })

  // 10. Top Languages (top 3)
  sections.push({
    id: 'languages',
    title: 'Top Languages',
    metrics: agg.languages && agg.languages.length > 0
      ? buildTopNMetrics(agg.languages, 3)
      : emptySlots(3),
  })

  // 11. Platform Mix (always show Instagram, TikTok, X, YouTube)
  const platformTotal = agg.platform.reduce((s, p) => s + p.count, 0)
  const platformMap = new Map(agg.platform.map((p) => [p.name.toLowerCase(), p.count]))
  sections.push({
    id: 'platform',
    title: 'Platform Mix',
    metrics: [
      { label: 'Instagram', value: toPercent(platformMap.get('instagram') ?? 0, platformTotal), format: 'percent' as const },
      { label: 'TikTok', value: toPercent(platformMap.get('tiktok') ?? 0, platformTotal), format: 'percent' as const },
      { label: 'X', value: toPercent(platformMap.get('x') ?? 0, platformTotal), format: 'percent' as const },
      { label: 'YouTube', value: toPercent(platformMap.get('youtube') ?? 0, platformTotal), format: 'percent' as const },
    ],
  })

  // 12. Top Teams (top 3)
  sections.push({
    id: 'teams',
    title: 'Top Teams',
    metrics: agg.teams && agg.teams.length > 0
      ? buildTopNMetrics(agg.teams, 3)
      : emptySlots(3),
  })

  // 13. Top Hashtags (top 3)
  sections.push({
    id: 'hashtags',
    title: 'Top Hashtags',
    metrics: agg.hashtags && agg.hashtags.length > 0
      ? buildTopNMetrics(agg.hashtags, 3, '#')
      : emptySlots(3),
  })

  // 14. Quality (all values are counts → convert to % of totalProfiles)
  const totalProfiles = agg.vanity.totalProfiles
  const qualityData = (agg as any).quality ?? {}
  sections.push({
    id: 'quality',
    title: 'Quality',
    metrics: [
      { label: 'Is Person', value: toPercent(qualityData.isPerson ?? 0, totalProfiles), format: 'percent' },
      { label: 'Verified', value: toPercent(agg.engagement.verified, totalProfiles), format: 'percent' },
      { label: 'Famous', value: toPercent(agg.engagement.famous, totalProfiles), format: 'percent' },
      {
        label: 'Spam Rate',
        value: agg.comments ? toPercent(agg.comments.spam_count, totalProfiles) : 0,
        format: 'percent',
      },
      {
        label: 'Toxic Rate',
        value: agg.comments ? toPercent(agg.comments.toxic_count, totalProfiles) : 0,
        format: 'percent',
      },
    ],
  })

  // If the other subject's aggregations are available, enrich with deltas
  if (otherAgg) {
    const otherSections = buildColumnSections(otherAgg)
    return sections.map((section) => {
      const otherSection = otherSections.find((s) => s.id === section.id)
      if (!otherSection) return section
      return {
        ...section,
        metrics: enrichWithDeltas(section.metrics, otherSection.metrics),
      }
    })
  }

  return sections
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ColumnPlaceholder({ variant }: { variant: 'A' | 'B' }) {
  return (
    <div className="bg-v11-surface border border-v11-border border-dashed rounded-lg p-4 flex flex-col items-center justify-center min-h-[300px]">
      <GitCompare className="h-10 w-10 text-v11-text-muted opacity-30 mb-3" />
      <p className="text-sm text-v11-text-muted">
        Select Subject {variant}
      </p>
    </div>
  )
}

function ColumnError({ variant, error }: { variant: 'A' | 'B'; error: Error }) {
  return (
    <div className="bg-v11-surface border border-v11-border rounded-lg p-4">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load Subject {variant}: {error.message}
        </AlertDescription>
      </Alert>
    </div>
  )
}

/**
 * Render a subject column. When otherAgg is provided, shows deltas/winners.
 */
function SubjectColumn({
  subject,
  otherAgg,
  variant,
}: {
  subject: SubjectWithAggregations
  otherAgg?: SubjectAggregations | null
  variant: 'blue' | 'purple'
}) {
  const sections = buildColumnSections(subject.aggregations, otherAgg)

  return (
    <ComparisonColumn
      subject={{
        name: subject.name,
        icon: subject.icon,
        count: subject.aggregations.vanity.totalProfiles,
      }}
      sections={sections}
      variant={variant}
    />
  )
}

function deriveSectionShapes(subject: SubjectWithAggregations): SectionShape[] {
  const sections = buildColumnSections(subject.aggregations)
  return sections.map((s) => ({ id: s.id, metricCount: s.metrics.length }))
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Progressive comparison view that shows data as it loads.
 * Always uses the same rendering path — deltas are overlaid when both sides are ready.
 */
export function ProgressiveComparisonView({
  state,
  className = '',
}: ProgressiveComparisonViewProps) {
  const {
    subjectA,
    isLoadingA,
    errorA,
    subjectB,
    isLoadingB,
    errorB,
  } = state

  // When both subjects are loaded, pass the other's aggregations for delta computation
  const otherAggForA = subjectB?.aggregations ?? null
  const otherAggForB = subjectA?.aggregations ?? null

  // Derive skeleton shapes from sibling (if loaded)
  const shapesFromA = subjectA ? deriveSectionShapes(subjectA) : undefined
  const shapesFromB = subjectB ? deriveSectionShapes(subjectB) : undefined

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${className}`}>
      {/* Column A */}
      {errorA ? (
        <ColumnError variant="A" error={errorA} />
      ) : isLoadingA ? (
        <ComparisonColumnSkeleton variant="blue" sectionShapes={shapesFromB} />
      ) : subjectA ? (
        <SubjectColumn subject={subjectA} otherAgg={otherAggForA} variant="blue" />
      ) : (
        <ColumnPlaceholder variant="A" />
      )}

      {/* Column B */}
      {errorB ? (
        <ColumnError variant="B" error={errorB} />
      ) : isLoadingB ? (
        <ComparisonColumnSkeleton variant="purple" sectionShapes={shapesFromA} />
      ) : subjectB ? (
        <SubjectColumn subject={subjectB} otherAgg={otherAggForB} variant="purple" />
      ) : (
        <ColumnPlaceholder variant="B" />
      )}
    </div>
  )
}

export default ProgressiveComparisonView
